import {
  createComponentAdmin,
  PublishesTo,
  SubscribesTo,
  type SchemaTypeHelper,
} from '@backstage-components/base';
import {Type, type Static} from '@sinclair/typebox';

const reactName = 'Audio';
const name = reactName;
const description =
  'The audio component defines a sound. Sounds can range from ambient background music to a sound effect played in response to user behavior.';

const schema = Type.Object({
  source: Type.Object(
    {
      assetId: Type.Optional(
        Type.String({
          title: 'Asset Id',
          description: 'Asset Id from the asset manager',
        })
      ),
      name: Type.Optional(
        Type.String({
          title: 'Audio Clip Name',
          description: 'Easily identifiable name for the audio clip',
        })
      ),
      url: Type.String({
        title: 'URL',
        description: 'Location from which the audio file will be loaded',
      }),
    },
    {title: 'Details'}
  ),
  shouldLoop: Type.Boolean({
    title: 'Loops',
    description:
      'Whether the audio file, once loaded, should loop until explicitly stopped',
  }),
  shouldAutoplay: Type.Boolean({
    title: 'Autoplay',
    description:
      "Start audio playback automatically, per guest device's a auto-play policy",
  }),
  preload: Type.Boolean({
    title: 'Preload',
    description: 'Start loading the audio file as soon as possible',
  }),
  playFromBeginning: Type.Boolean({
    title: 'Play From Beginning',
    description:
      'When the "play" instruction is called, start from the beginning of the audio file',
  }),
  audioVolume: Type.Number({
    title: 'Audio Volume',
    description: 'Volume level for  the Audio',
    default: 1,
    minimum: 0,
    maximum: 1,
    multipleOf: 0.01,
  }),
});

const uiSchema = {
  'ui:groups': {
    'ui:template': 'tabs',
    sections: [
      [
        'Properties',
        [
          {
            'ui:template': 'accordion',
            sections: [
              [
                'Audio Properties',
                [
                  'source',
                  'shouldLoop',
                  'preload',
                  'audioVolume',
                  'shouldAutoplay',
                  'playFromBeginning',
                ],
              ],
            ],
          },
        ],
      ],
      ['Styling', []],
      ['Animations', []],
    ],
  },
  source: {
    'ui:field': 'AudioField',
    'ui:options': {
      assetType: 'audio',
      buttonTitle: 'Select Audio Asset',
    },
  },
  audioVolume: {
    'ui:widget': 'range',
    'ui:options': {
      type: 'percentage',
    },
  },
};

const defaultFieldData: Static<typeof schema> = {
  shouldAutoplay: false,
  shouldLoop: false,
  playFromBeginning: false,
  preload: true,
  audioVolume: 1,
  source: {
    url: 'https://assets.lcdbackstage.com/7c4e4973-7025-4582-992b-3e47056cabaa/43ad5ac4-c75f-411b-bbe4-891c3f203bb4.wav',
  },
};

const instructions = Type.Union([
  PublishesTo({
    topic: `${reactName}:on-complete`,
    description:
      'Audio has played through to the end. Does not trigger for looping audio.',
  }),
  PublishesTo({
    topic: `${reactName}:on-mute`,
    description: 'Audio playback volume has been set to zero.',
  }),
  PublishesTo({
    topic: `${reactName}:on-pause`,
    description: 'Audio playback has been paused.',
  }),
  PublishesTo({
    topic: `${reactName}:on-start`,
    description: 'Audio playback has started.',
  }),
  PublishesTo({
    topic: `${reactName}:on-unmute`,
    description: 'Audio playback volume has been unset from zero.',
  }),
  SubscribesTo({
    topic: `${reactName}:mute`,
    description: 'Set the audio playback volume to the zero.',
  }),
  SubscribesTo({
    topic: `${reactName}:pause`,
    description: 'Pause audio playback.',
  }),
  SubscribesTo({
    topic: `${reactName}:play`,
    description: 'Start audio playback.',
  }),
  SubscribesTo({
    topic: `${reactName}:unmute`,
    description: 'Set the audio playback volume to the previous level.',
  }),
]);

export const ComponentDefinition = createComponentAdmin({
  id: '22e08b91-3232-477a-9c0e-8d1fafa1f6b1',
  reactName,
  name,
  category: 'media',
  description,
  defaultFieldData,
  instructions,
  schema,
  slotConfiguration: {},
  slug: reactName,
  uiSchema,
  version: 1,
}).build();

export type SchemaType = SchemaTypeHelper<typeof ComponentDefinition>;
